/**
 * ChannelIconBadge - Shared channel type icon component.
 *
 * Renders the appropriate channel icon (Zapy, Telegram, Email) with optional
 * tooltip showing connection name and provider. Used in both TicketListItem
 * and ConversationListItem for consistent channel display.
 *
 * Supports two display modes:
 * - `badge`: Small icon overlay (e.g., on an avatar corner)
 * - `inline`: Inline icon for use within text rows
 */

import { Box, Tooltip } from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import { ZapyIcon } from '../icons/ZapyIcon';
import { ChannelType, getChannelTypeName } from '../../types/chat.types';

/** Color mapping for SVG-based channel icons (ZapyIcon is a PNG with its own colors) */
const CHANNEL_ICON_COLORS: Partial<Record<ChannelType, string>> = {
  [ChannelType.TELEGRAM]: '#0088cc',
  [ChannelType.EMAIL]: '#EA4335',
};

export interface ChannelIconBadgeProps {
  /** Channel type to display */
  channelType?: ChannelType | null;
  /** Channel connection name (e.g., "Main WhatsApp") */
  connectionName?: string | null;
  /** Human-readable channel type name override */
  channelTypeName?: string | null;
  /** Display mode: badge (avatar overlay) or inline */
  mode?: 'badge' | 'inline';
  /** Icon size in px */
  size?: number;
}

/**
 * Build tooltip text from connection name and channel type name.
 * Format: "Connection Name · Provider" or just one of them.
 */
function buildTooltip(
  connectionName?: string | null,
  channelTypeName?: string | null,
): string {
  if (connectionName && channelTypeName) {
    return `${connectionName} · ${channelTypeName}`;
  }
  return connectionName || channelTypeName || '';
}

/**
 * Render the icon element for a given channel type.
 */
function renderIcon(channelType: ChannelType, size: number) {
  const iconSx = { fontSize: size, color: CHANNEL_ICON_COLORS[channelType] ?? 'text.secondary' };

  switch (channelType) {
    case ChannelType.ZAPY:
      return <ZapyIcon fontSize={size} />;
    case ChannelType.TELEGRAM:
      return <TelegramIcon sx={iconSx} />;
    case ChannelType.EMAIL:
      return <EmailIcon sx={iconSx} />;
    default:
      return null;
  }
}

/**
 * ChannelIconBadge component.
 *
 * @example Badge mode (avatar overlay):
 * ```tsx
 * <Box sx={{ position: 'relative' }}>
 *   <Avatar />
 *   <ChannelIconBadge channelType={ChannelType.ZAPY} connectionName="Main" mode="badge" />
 * </Box>
 * ```
 *
 * @example Inline mode:
 * ```tsx
 * <ChannelIconBadge channelType={ChannelType.TELEGRAM} mode="inline" size={14} />
 * ```
 */
export function ChannelIconBadge({
  channelType,
  connectionName,
  channelTypeName: channelTypeNameProp,
  mode = 'badge',
  size = 16,
}: ChannelIconBadgeProps) {
  if (!channelType) return null;

  const resolvedTypeName = channelTypeNameProp || getChannelTypeName(channelType);
  const tooltip = buildTooltip(connectionName, resolvedTypeName);
  const icon = renderIcon(channelType, size);

  if (!icon) return null;

  if (mode === 'badge') {
    return (
      <Tooltip title={tooltip} placement="bottom">
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: size + 4,
            height: size + 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  }

  // Inline mode
  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {icon}
      </Box>
    </Tooltip>
  );
}
